/**
 * Shared *visual* layer for the commitment lifecycle.
 *
 * `commitment.ts` owns the vocabulary (Locked In / Bailed / Pending …); this
 * module owns the single visual treatment for those states so every surface —
 * cards, chips, timelines, summary bars — looks identical. No component should
 * hard-code its own commitment colours.
 *
 * All classes are static Tailwind strings built on existing semantic tokens.
 */

import { commitmentStatus, hoursUntil, type CommitmentKey } from "@/lib/commitment";

export interface CommitmentVisual {
  key: CommitmentKey;
  label: string;
  emoji: string;
  /** Lucide icon name — components map this to a real component. */
  icon: "lock" | "door-open" | "help-circle" | "x" | "clock";
  /** Chip / badge treatment (bg + text + border). */
  chip: string;
  /** Card / container surface treatment. */
  surface: string;
  /** Solid fill used by meters and legend dots. */
  fill: string;
  /** Icon tile treatment. */
  iconTile: string;
}

const VISUALS: Record<CommitmentKey, CommitmentVisual> = {
  locked_in: {
    key: "locked_in",
    label: "Locked In",
    emoji: "🔒",
    icon: "lock",
    chip: "bg-primary/15 text-primary border-primary/40",
    surface: "border-primary/50 bg-primary/5",
    fill: "bg-primary",
    iconTile: "bg-primary/15 text-primary",
  },
  bailed: {
    key: "bailed",
    label: "Bailed",
    emoji: "👻",
    icon: "door-open",
    // Recovery, not failure: soft neutral, never destructive red.
    chip: "bg-muted text-muted-foreground border-border/60",
    surface: "border-border/60 bg-muted/40",
    fill: "bg-muted-foreground/50",
    iconTile: "bg-muted text-muted-foreground",
  },
  maybe: {
    key: "maybe",
    label: "Maybe",
    emoji: "🤔",
    icon: "help-circle",
    chip: "bg-secondary/40 text-secondary-foreground border-secondary",
    surface: "border-secondary bg-secondary/15",
    fill: "bg-secondary",
    iconTile: "bg-secondary/30 text-secondary-foreground",
  },
  declined: {
    key: "declined",
    label: "Can't Make It",
    emoji: "🙅",
    icon: "x",
    chip: "bg-destructive/10 text-destructive border-destructive/40",
    surface: "border-destructive/30 bg-destructive/5",
    fill: "bg-destructive/60",
    iconTile: "bg-destructive/10 text-destructive",
  },
  pending: {
    key: "pending",
    label: "Pending",
    emoji: "⏳",
    icon: "clock",
    chip: "bg-muted/70 text-muted-foreground border-border/50",
    surface: "border-border/50 bg-muted/20",
    fill: "bg-muted-foreground/30",
    iconTile: "bg-muted text-muted-foreground",
  },
};

/** The single source of truth for how a commitment state looks. */
export function commitmentVisual(status?: string | null): CommitmentVisual {
  return VISUALS[commitmentStatus(status).key];
}

export function commitmentVisualByKey(key: CommitmentKey): CommitmentVisual {
  return VISUALS[key];
}

/**
 * Countdown emphasis. Far out, days/hours carry the message and
 * minutes/seconds are noise; close in, the small units matter.
 */
export type CountdownEmphasis = "days" | "hours" | "minutes";

export function countdownEmphasis(
  planAt: Date,
  now: Date = new Date(),
): CountdownEmphasis {
  const h = hoursUntil(planAt, now);
  if (h >= 48) return "days";
  if (h >= 6) return "hours";
  return "minutes";
}

/** Which countdown units to render, most significant first. */
export function countdownUnits(
  emphasis: CountdownEmphasis,
): Array<"days" | "hours" | "minutes" | "seconds"> {
  switch (emphasis) {
    case "days":
      return ["days", "hours"];
    case "hours":
      return ["hours", "minutes"];
    default:
      return ["hours", "minutes", "seconds"];
  }
}

/** True once the plan is close enough that the card should feel urgent. */
export function isPlanImminent(planAt: Date, now: Date = new Date()): boolean {
  const h = hoursUntil(planAt, now);
  return h > 0 && h <= 6;
}

/** Accurate reliability cue — commitment signal, never a claimed score bump. */
export const RELIABILITY_CUE = "Locked In → follow through to strengthen reliability";
